import { useEffect, useRef, useState } from 'react';
import type { Wallboard } from '../api/client';

/**
 * Sounds for the wallboard: a different one for each kind of thing that happens (a caller starts
 * waiting, a queue turns amber or red, nobody is ready, a caller hangs up...). Made by the browser
 * (Web Audio), so there are no sound files to load.
 */

export type BoardSound = 'caller' | 'busy' | 'urgent' | 'noAgents' | 'hungUp' | 'parked' | 'answered' | 'clear' | 'offline' | 'texts';
export type SoundVolume = 'low' | 'medium' | 'high';

/** Every sound, most urgent first (when several happen at once, only the most urgent plays). */
export const BOARD_SOUNDS: { id: BoardSound; label: string; hint: string }[] = [
  { id: 'urgent', label: 'A queue turns red', hint: 'An alarm, again every minute while it stays red' },
  { id: 'noAgents', label: 'Callers wait and nobody is ready', hint: 'A low buzz, again every minute' },
  { id: 'offline', label: 'The board loses its connection', hint: 'Three falling notes' },
  { id: 'busy', label: 'A queue turns amber', hint: 'Three short beeps' },
  { id: 'hungUp', label: 'A caller hangs up waiting', hint: 'Two falling notes' },
  { id: 'caller', label: 'A caller starts waiting', hint: 'A soft two-note chime' },
  { id: 'texts', label: 'A text starts waiting', hint: 'Two quick high notes, for a ring group’s or queue’s texts' },
  { id: 'parked', label: 'A call is parked', hint: 'Two quick high pings' },
  { id: 'clear', label: 'Everything is back to normal', hint: 'Three rising notes' },
  { id: 'answered', label: 'A call is answered', hint: 'A quiet tick (busy queues: leave it off)' }
];
const PRIORITY: BoardSound[] = BOARD_SOUNDS.map((s) => s.id);
/** On unless someone turns them off (the quieter ones start off, and texts: 1.12.33, off unless chosen). */
export const DEFAULT_SOUNDS: BoardSound[] = ['urgent', 'noAgents', 'offline', 'busy', 'hungUp', 'caller', 'parked'];
export const VOLUMES: { id: SoundVolume; label: string }[] = [
  { id: 'low', label: 'Quiet' },
  { id: 'medium', label: 'Medium' },
  { id: 'high', label: 'Loud' }
];

/** At most this often (ms), however often it happens. */
const COOLDOWN: Record<BoardSound, number> = {
  urgent: 10_000,
  noAgents: 10_000,
  offline: 60_000,
  busy: 20_000,
  hungUp: 4_000,
  caller: 8_000,
  texts: 8_000,
  parked: 8_000,
  clear: 30_000,
  answered: 1_500
};
/** Still red, or still nobody ready: say so again this often. */
export const REPEAT_MS = 60_000;

type Note = { f: number; t: number; d: number; type?: OscillatorType; g?: number };
const PATTERNS: Record<BoardSound, Note[]> = {
  caller: [
    { f: 659.25, t: 0, d: 0.25 },
    { f: 880, t: 0.18, d: 0.4 }
  ],
  busy: [0, 0.18, 0.36].map((t) => ({ f: 784, t, d: 0.12, type: 'triangle' as const })),
  urgent: [988, 740, 988, 740, 988, 740].map((f, i) => ({ f, t: i * 0.17, d: 0.15, type: 'square' as const, g: 0.35 })),
  noAgents: [0, 0.4, 0.8].map((t) => ({ f: 196, t, d: 0.28, type: 'sawtooth' as const, g: 0.45 })),
  hungUp: [
    { f: 440, t: 0, d: 0.22 },
    { f: 330, t: 0.22, d: 0.4 }
  ],
  parked: [
    { f: 1175, t: 0, d: 0.09 },
    { f: 1175, t: 0.14, d: 0.09 }
  ],
  answered: [{ f: 1320, t: 0, d: 0.06, g: 0.35 }],
  // A text starts waiting (1.12.33): two quick high notes, a step apart (higher and quicker than a caller's).
  texts: [
    { f: 1318.5, t: 0, d: 0.09, g: 0.6 },
    { f: 1760, t: 0.11, d: 0.18, g: 0.6 }
  ],
  clear: [
    { f: 523.25, t: 0, d: 0.18 },
    { f: 659.25, t: 0.14, d: 0.18 },
    { f: 783.99, t: 0.28, d: 0.35 }
  ],
  offline: [
    { f: 523.25, t: 0, d: 0.2, type: 'triangle' },
    { f: 392, t: 0.2, d: 0.2, type: 'triangle' },
    { f: 261.63, t: 0.4, d: 0.4, type: 'triangle' }
  ]
};
const LEVEL: Record<SoundVolume, number> = { low: 0.12, medium: 0.3, high: 0.6 };

type AudioCtor = typeof AudioContext;

/** Plays the board's sounds. Browsers only start sound after someone clicks the page once. */
export class BoardChimes {
  private ctx: AudioContext | null = null;
  private busyUntil = 0;
  volume: SoundVolume = 'medium';

  private context(): AudioContext | null {
    if (this.ctx) return this.ctx;
    const Ctor = (window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioCtor }).webkitAudioContext) as AudioCtor | undefined;
    if (!Ctor) return null;
    try {
      this.ctx = new Ctor();
    } catch {
      this.ctx = null;
    }
    return this.ctx;
  }

  /** Whether sound can play now (false until the page has been clicked, in most browsers). */
  get ready(): boolean {
    return this.context()?.state === 'running';
  }

  /** From a click: lets sound play from now on. */
  async unlock(): Promise<boolean> {
    const ctx = this.context();
    if (!ctx) return false;
    try {
      if (ctx.state !== 'running') await ctx.resume();
    } catch {
      return false;
    }
    return ctx.state === 'running';
  }

  /** Plays one sound after any already playing; skipped when blocked or too many are lined up. */
  play(sound: BoardSound): boolean {
    const ctx = this.context();
    if (!ctx || ctx.state !== 'running') return false;
    const now = ctx.currentTime;
    if (this.busyUntil - now > 3) return false;
    const start = Math.max(now + 0.02, this.busyUntil + 0.08);
    const master = ctx.createGain();
    master.gain.value = LEVEL[this.volume];
    master.connect(ctx.destination);
    let end = start;
    for (const n of PATTERNS[sound]) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = n.type ?? 'sine';
      osc.frequency.value = n.f;
      const t0 = start + n.t;
      const peak = n.g ?? 0.8;
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.linearRampToValueAtTime(peak, t0 + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + n.d);
      osc.connect(g).connect(master);
      osc.start(t0);
      osc.stop(t0 + n.d + 0.02);
      end = Math.max(end, t0 + n.d);
    }
    this.busyUntil = end;
    return true;
  }
}

/** What the board looked like, to tell what just changed. */
export interface BoardMemory {
  queues: Record<string, { waiting: number; abandoned: number; answered: number; level: 0 | 1 | 2; noOne: boolean; redAt: number; noOneAt: number }>;
  parked: number;
  offline: boolean;
  last: Partial<Record<BoardSound, number>>;
  /** Texts waiting for an answer, per ring group or queue (1.12.33). Missing from a memory made before: none known. */
  texts?: Record<string, number>;
}

/** A ring group's or queue's texts on the board (1.12.33): how many wait for an answer. */
export interface BoardTexts {
  extension: string;
  waiting: number;
}

export interface Limits {
  waiting: number;
  waitSeconds: number;
}

/** 0 calm, 1 amber, 2 red: the same rule the tiles use. */
function levelOf(value: number, limit: number): 0 | 1 | 2 {
  if (value >= limit * 2) return 2;
  if (value >= limit) return 1;
  return 0;
}

/**
 * What to play now, given the board and what it looked like before. Pure, so it can be tested:
 * the first board only sets the baseline; counts going down (a new day) play nothing.
 */
export function boardSounds(
  prev: BoardMemory | null,
  board: Pick<Wallboard, 'queues' | 'parked'>,
  opts: { limits: Limits; since: number; offline: boolean; now: number; events: readonly BoardSound[]; texts?: readonly BoardTexts[] | null }
): { play: BoardSound | null; memory: BoardMemory } {
  const want = new Set(opts.events);
  const heard: BoardSound[] = [];
  const queues: BoardMemory['queues'] = {};
  let anyAlert = false;
  let wasAlert = false;
  for (const q of board.queues) {
    const before = prev?.queues[q.extension];
    const longest = q.longestWaitSeconds !== undefined && q.waiting > 0 ? q.longestWaitSeconds + opts.since : 0;
    const level = Math.max(levelOf(q.waiting, opts.limits.waiting), levelOf(longest, opts.limits.waitSeconds)) as 0 | 1 | 2;
    const noOne = q.waiting > 0 && !q.agents.some((a) => a.state === 'available');
    const now = {
      waiting: q.waiting,
      abandoned: q.abandonedToday ?? 0,
      answered: q.answeredToday ?? 0,
      level,
      noOne,
      redAt: before?.redAt ?? 0,
      noOneAt: before?.noOneAt ?? 0
    };
    if (before) {
      if (level === 2 && (before.level < 2 || opts.now - before.redAt >= REPEAT_MS)) {
        heard.push('urgent');
        now.redAt = opts.now;
      } else if (level === 1 && before.level === 0) heard.push('busy');
      if (noOne && (!before.noOne || opts.now - before.noOneAt >= REPEAT_MS)) {
        heard.push('noAgents');
        now.noOneAt = opts.now;
      }
      if (now.waiting > before.waiting) heard.push('caller');
      if (now.abandoned > before.abandoned) heard.push('hungUp');
      if (now.answered > before.answered) heard.push('answered');
      if (before.level > 0 || before.noOne) wasAlert = true;
    } else {
      // A queue that just appeared: its state is the baseline.
      if (level === 2) now.redAt = opts.now;
      if (noOne) now.noOneAt = opts.now;
    }
    if (level > 0 || noOne) anyAlert = true;
    queues[q.extension] = now;
  }
  // Texts (1.12.33): a team's count of texts waiting went up. A team that just appeared, or texts
  // not read this time (an older server, a hiccup): the baseline, nothing played.
  const texts: Record<string, number> = {};
  for (const t of opts.texts ?? []) {
    texts[t.extension] = t.waiting;
    const before = prev?.texts?.[t.extension];
    if (before !== undefined && t.waiting > before) heard.push('texts');
  }
  const parked = board.parked.reduce((n, p) => n + p.calls, 0);
  if (prev) {
    if (parked > prev.parked) heard.push('parked');
    if (opts.offline && !prev.offline) heard.push('offline');
    if (wasAlert && !anyAlert) heard.push('clear');
  }
  const last = { ...(prev?.last ?? {}) };
  // The most urgent one that's switched on and not heard too recently.
  const play = prev
    ? (PRIORITY.find((s) => heard.includes(s) && want.has(s) && opts.now - (last[s] ?? 0) >= COOLDOWN[s]) ?? null)
    : null;
  if (play) last[play] = opts.now;
  // Texts not read this time: what was known stays, so a hiccup doesn't sound like new texts after.
  return { play, memory: { queues, parked, offline: opts.offline, last, texts: opts.texts ? texts : (prev?.texts ?? {}) } };
}

export interface SoundChoices {
  on: boolean;
  volume: SoundVolume;
  events: readonly BoardSound[];
}

/**
 * Plays the board's sounds while `choices.on`. Returns whether a click is needed before sound can
 * play (browsers block it until the page is clicked), and `unlock` for that click.
 */
export function useBoardSounds(board: Pick<Wallboard, 'at' | 'queues' | 'parked'> | null, choices: SoundChoices, limits: Limits, since: number, offline: boolean, texts?: readonly BoardTexts[] | null) {
  const chimes = useRef<BoardChimes | null>(null);
  const memory = useRef<BoardMemory | null>(null);
  const [needsClick, setNeedsClick] = useState(false);
  if (!chimes.current && typeof window !== 'undefined') chimes.current = new BoardChimes();
  if (chimes.current) chimes.current.volume = choices.volume;

  // Off, or switched on again later: start from what the board shows then (no burst of old news).
  useEffect(() => {
    if (!choices.on) memory.current = null;
  }, [choices.on]);

  // Any click on the page lets sound play (a TV usually gets one, for Full screen).
  useEffect(() => {
    if (!choices.on) return;
    const c = chimes.current;
    if (!c) return;
    setNeedsClick(!c.ready);
    const onClick = () => void c.unlock().then((ok) => setNeedsClick(!ok));
    document.addEventListener('pointerdown', onClick);
    document.addEventListener('keydown', onClick);
    return () => {
      document.removeEventListener('pointerdown', onClick);
      document.removeEventListener('keydown', onClick);
    };
  }, [choices.on]);

  const events = choices.events.join(',');
  const sinceStep = Math.floor(since);
  // Texts waiting per team (1.12.33), as a key: a new reading with the same counts changes nothing.
  const textKey = texts ? texts.map((t) => `${t.extension}:${t.waiting}`).join(',') : '';
  useEffect(() => {
    if (!choices.on || !board) return;
    const r = boardSounds(memory.current, board, { limits, since, offline, now: Date.now(), events: choices.events, texts: texts ?? null });
    memory.current = r.memory;
    if (r.play) chimes.current?.play(r.play);
    // Re-evaluated each second (waits grow between refreshes) and on each new reading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, choices.on, events, limits.waiting, limits.waitSeconds, sinceStep, offline, textKey]);

  return {
    needsClick: choices.on && needsClick,
    unlock: async () => {
      const ok = (await chimes.current?.unlock()) ?? false;
      setNeedsClick(!ok);
      return ok;
    },
    /** Plays one now (settings: "Play"). */
    preview: async (sound: BoardSound) => {
      const c = chimes.current;
      if (!c) return;
      await c.unlock();
      c.play(sound);
    }
  };
}

/** Plays one sound once, at a volume, from a click (the settings' "Play" buttons). */
let previewChimes: BoardChimes | null = null;
export async function previewSound(sound: BoardSound, volume: SoundVolume): Promise<void> {
  previewChimes ??= new BoardChimes();
  previewChimes.volume = volume;
  await previewChimes.unlock();
  previewChimes.play(sound);
}
